import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Document, Page, pdfjs } from 'react-pdf';
import './PdfEditor.css';

// ✅ Use CDN worker with the exact version we know works
pdfjs.GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

function PdfUploadEditor({ onComplete, onCancel }) {
  const [pdfData, setPdfData] = useState(null);
  const [originalFile, setOriginalFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [numPages, setNumPages] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [scale, setScale] = useState(1.3);
  const [placedFields, setPlacedFields] = useState([]);
  const [draggedFieldType, setDraggedFieldType] = useState(null);
  const [movingFieldId, setMovingFieldId] = useState(null); // Track which field is being moved
  const [resizingFieldId, setResizingFieldId] = useState(null); // Track which field is being resized
  const [moveStartPos, setMoveStartPos] = useState({ mouseX: 0, mouseY: 0, fieldX: 0, fieldY: 0 });
  const [resizeStartPos, setResizeStartPos] = useState({ x: 0, y: 0, width: 0, height: 0 });
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });
  const [loadError, setLoadError] = useState(null);

  const pageRef = useRef(null);
  const dropZoneRef = useRef(null);

  // ✅ Show detailed version info
  console.log('PDF.js version:', pdfjs.version);
  console.log('Worker URL:', pdfjs.GlobalWorkerOptions.workerSrc);

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (file.type !== 'application/pdf') {
      alert('Please upload a PDF file');
      return;
    }

    setLoadError(null);
    setOriginalFile(file);
    setFileName(file.name);
    setPlacedFields([]);
    setCurrentPage(1);
    setNumPages(null);

    const reader = new FileReader();
    reader.onload = (e) => {
      console.log('PDF loaded, bytes:', e.target.result.byteLength);
      setPdfData(e.target.result);
    };
    reader.onerror = (e) => {
      console.error('FileReader error:', e);
      setLoadError('Could not read file: ' + e.toString());
    };
    reader.readAsArrayBuffer(file);
  };

  const measureCanvas = useCallback(() => {
    if (!dropZoneRef.current) return;
    const canvas = dropZoneRef.current.querySelector('canvas');
    if (canvas) {
      const rect = canvas.getBoundingClientRect();
      setCanvasSize({ width: rect.width, height: rect.height });
    }
  }, []);

  useEffect(() => {
    if (!pdfData) return;
    const timer = setTimeout(measureCanvas, 300);
    window.addEventListener('resize', measureCanvas);
    return () => {
      clearTimeout(timer);
      window.removeEventListener('resize', measureCanvas);
    };
  }, [pdfData, currentPage, scale, measureCanvas]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    console.log('✅ PDF loaded successfully, pages:', numPages);
    setNumPages(numPages);
    setLoadError(null);
  };

  const onDocumentLoadError = (error) => {
    console.error('❌ PDF load error:', error);
    // Show the actual error message
    const errorMsg = error?.message || error?.toString() || 'Unknown error';
    setLoadError(`PDF Error: ${errorMsg}`);
  };

  const onPageRenderSuccess = () => {
    console.log('✅ Page rendered');
    setTimeout(measureCanvas, 100);
  };

  const handleDragStart = (e, fieldType) => {
    setDraggedFieldType(fieldType);
    e.dataTransfer.effectAllowed = 'copy';
  };

  const handleDrop = (e) => {
    e.preventDefault();
    
    const canvas = dropZoneRef.current?.querySelector('canvas');
    if (!canvas) return;

    const canvasRect = canvas.getBoundingClientRect();
    const x = e.clientX - canvasRect.left;
    const y = e.clientY - canvasRect.top;

    // Only allow drops within canvas bounds
    if (x < 0 || y < 0 || x > canvasRect.width || y > canvasRect.height) {
      setDraggedFieldType(null);
      return;
    }

    const percentX = (x / canvasRect.width) * 100;
    const percentY = (y / canvasRect.height) * 100;

    // Only handle new field placement from palette
    if (draggedFieldType) {
      const newField = {
        id: `field_${Date.now()}`,
        type: draggedFieldType,
        page: currentPage,
        x: Math.max(0, Math.min(95, percentX)),
        y: Math.max(0, Math.min(97, percentY)),
        width: draggedFieldType === 'SIGNATURE' ? 25 : 12,
        height: draggedFieldType === 'SIGNATURE' ? 4 : 3,
      };
      setPlacedFields(prev => [...prev, newField]);
      setDraggedFieldType(null);
    }
  };

  const handleFieldMoveStart = (e, fieldId, field) => {
    e.stopPropagation();
    e.preventDefault();
    setMovingFieldId(fieldId);
    setMoveStartPos({
      mouseX: e.clientX,
      mouseY: e.clientY,
      fieldX: field.x,
      fieldY: field.y
    });
  };

  const handleResizeStart = (e, fieldId, field) => {
    e.stopPropagation();
    e.preventDefault();
    setResizingFieldId(fieldId);
    setResizeStartPos({
      x: e.clientX,
      y: e.clientY,
      width: field.width,
      height: field.height
    });
  };

  const handleMouseMove = useCallback((e) => {
    const canvas = dropZoneRef.current?.querySelector('canvas');
    if (!canvas) return;
    const canvasRect = canvas.getBoundingClientRect();

    // Handle moving
    if (movingFieldId) {
      const deltaX = e.clientX - moveStartPos.mouseX;
      const deltaY = e.clientY - moveStartPos.mouseY;
      
      const deltaXPercent = (deltaX / canvasRect.width) * 100;
      const deltaYPercent = (deltaY / canvasRect.height) * 100;
      
      setPlacedFields(prev => prev.map(field => 
        field.id === movingFieldId
          ? {
              ...field,
              x: Math.max(0, Math.min(95, moveStartPos.fieldX + deltaXPercent)),
              y: Math.max(0, Math.min(97, moveStartPos.fieldY + deltaYPercent))
            }
          : field
      ));
    }

    // Handle resizing
    if (resizingFieldId) {
      const deltaX = e.clientX - resizeStartPos.x;
      const deltaY = e.clientY - resizeStartPos.y;
      
      const deltaWidthPercent = (deltaX / canvasRect.width) * 100;
      const deltaHeightPercent = (deltaY / canvasRect.height) * 100;
      
      setPlacedFields(prev => prev.map(field => 
        field.id === resizingFieldId
          ? {
              ...field,
              width: Math.max(8, Math.min(50, resizeStartPos.width + deltaWidthPercent)),
              height: Math.max(2, Math.min(15, resizeStartPos.height + deltaHeightPercent))
            }
          : field
      ));
    }
  }, [movingFieldId, resizingFieldId, moveStartPos, resizeStartPos]);

  const handleMouseUp = useCallback(() => {
    setMovingFieldId(null);
    setResizingFieldId(null);
  }, []);

  // Add mouse event listeners for move and resize
  useEffect(() => {
    if (movingFieldId || resizingFieldId) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [movingFieldId, resizingFieldId, handleMouseMove, handleMouseUp]);

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'copy';
  };

  const removeField = (fieldId) => {
    setPlacedFields(prev => prev.filter(f => f.id !== fieldId));
  };

  const handleComplete = () => {
    if (!originalFile) {
      alert('Please upload a PDF first');
      return;
    }
    if (placedFields.length === 0) {
      alert('Please drag at least one signature field onto the PDF');
      return;
    }
    onComplete({ file: originalFile, fields: placedFields });
  };

  const fieldLabel = (type) => {
    if (type === 'SIGNATURE') return { icon: '✍️', name: 'Signature' };
    if (type === 'DATE') return { icon: '📅', name: 'Date' };
    return { icon: '🔤', name: 'Initials' };
  };

  return (
    <div className="pdf-upload-editor">
      <h4>Upload Additional Document for Signing</h4>

      {/* Version info for debugging */}
      <div style={{ fontSize: 11, color: '#888', marginBottom: 10 }}>
        PDF.js v{pdfjs.version}
      </div>

      {!pdfData && (
        <div className="upload-section">
          <label className="upload-button">
            <input
              type="file"
              accept=".pdf"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
            />
            📄 Choose PDF File
          </label>
          <p className="upload-hint">
            Upload title, lien release, or any document requiring signatures
          </p>
          {loadError && (
            <div className="pdf-error-box" style={{ marginTop: 15 }}>
              <p><strong>⚠️ Error Details:</strong></p>
              <p style={{ fontSize: 12, wordBreak: 'break-word' }}>{loadError}</p>
            </div>
          )}
        </div>
      )}

      {pdfData && (
        <div className="pdf-editor-container" style={{ display: 'flex', width: '100%', gap: '16px' }}>
          <div className="pdf-viewer-section" style={{ flex: '1 1 70%', minWidth: 0 }}>
            <div className="pdf-filename">
              <span>✓ {fileName}</span>
              <button
                onClick={() => { setPdfData(null); setOriginalFile(null); setPlacedFields([]); }}
                className="btn-link"
              >
                Change File
              </button>
            </div>

            {loadError && (
              <div className="pdf-error-box">
                <p><strong>⚠️ Error Details:</strong></p>
                <p style={{ fontSize: 12, wordBreak: 'break-word' }}>{loadError}</p>
                <p style={{ fontSize: 11, marginTop: 10 }}>
                  Open browser console (F12) for more details
                </p>
              </div>
            )}

            <div
              className="pdf-canvas-container"
              ref={dropZoneRef}
              onDrop={handleDrop}
              onDragOver={handleDragOver}
            >
              <Document
                file={pdfData}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={<div className="pdf-loading">⏳ Loading PDF...</div>}
                error={<div className="pdf-loading">❌ Failed to load PDF</div>}
              >
                <Page
                  pageNumber={currentPage}
                  scale={scale}
                  onRenderSuccess={onPageRenderSuccess}
                  renderTextLayer={false}
                  renderAnnotationLayer={false}
                />
              </Document>

              {canvasSize.width > 0 && (() => {
                const canvas = dropZoneRef.current?.querySelector('canvas');
                if (!canvas) return null;
                const canvasRect = canvas.getBoundingClientRect();
                const containerRect = dropZoneRef.current.getBoundingClientRect();
                const offsetLeft = canvasRect.left - containerRect.left;
                const offsetTop = canvasRect.top - containerRect.top;
                return placedFields
                  .filter(f => f.page === currentPage)
                  .map(field => {
                    const { icon } = fieldLabel(field.type);
                    return (
                      <div
                        key={field.id}
                        className={`field-overlay field-overlay-${field.type.toLowerCase()}`}
                        style={{
                          left:   `${offsetLeft + (field.x / 100) * canvasRect.width}px`,
                          top:    `${offsetTop + (field.y / 100) * canvasRect.height}px`,
                          width:  `${(field.width / 100) * canvasRect.width}px`,
                          height: `${(field.height / 100) * canvasRect.height}px`,
                          cursor: movingFieldId === field.id ? 'grabbing' : 'grab',
                        }}
                        onMouseDown={(e) => {
                          if (!e.target.classList.contains('field-resize-handle') && 
                              !e.target.classList.contains('field-remove')) {
                            handleFieldMoveStart(e, field.id, field);
                          }
                        }}
                        title="Click and drag to move • Drag corner to resize • Click × to remove"
                      >
                        <span className="field-overlay-icon">{icon}</span>
                        <button
                          className="field-remove"
                          onClick={(e) => {
                            e.stopPropagation();
                            removeField(field.id);
                          }}
                        >
                          ×
                        </button>
                        <div
                          className="field-resize-handle"
                          onMouseDown={(e) => handleResizeStart(e, field.id, field)}
                          title="Drag to resize"
                        />
                      </div>
                    );
                  });
              })()}

              {(draggedFieldType || movingFieldId) && (
                <div className="drop-hint">{movingFieldId ? 'Release to place here' : 'Drop to place field'}</div>
              )}
            </div>

            {numPages > 1 && (
              <div className="page-controls">
                <button
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="btn-secondary"
                >
                  ← Prev
                </button>
                <span className="page-indicator">
                  Page {currentPage} of {numPages}
                </span>
                <button
                  onClick={() => setCurrentPage(p => Math.min(numPages, p + 1))}
                  disabled={currentPage === numPages}
                  className="btn-secondary"
                >
                  Next →
                </button>
              </div>
            )}

            <div className="zoom-controls">
              <button onClick={() => setScale(s => Math.max(0.5, +(s - 0.2).toFixed(1)))} className="btn-zoom">−</button>
              <span>{Math.round(scale * 100)}%</span>
              <button onClick={() => setScale(s => Math.min(2.5, +(s + 0.2).toFixed(1)))} className="btn-zoom">+</button>
              <button onClick={() => setScale(1.3)} className="btn-zoom-reset">Reset</button>
            </div>
          </div>

          <div className="field-palette-section" style={{ flex: '0 0 28%', minWidth: '220px' }}>
            <h5>Drag Fields to PDF</h5>
            <p className="palette-hint">
              Drag a field type and drop it on the PDF where the customer should sign.
            </p>

            <div className="draggable-fields">
              {[
                { type: 'SIGNATURE', icon: '✍️', label: 'Signature', color: 'green' },
                { type: 'DATE',      icon: '📅', label: 'Date',      color: 'blue' },
                { type: 'TEXT',      icon: '🔤', label: 'Initials',  color: 'orange' },
              ].map(({ type, icon, label, color }) => (
                <div
                  key={type}
                  className={`draggable-field ${color}-field`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, type)}
                  onDragEnd={() => setDraggedFieldType(null)}
                >
                  <span className="field-icon">{icon}</span>
                  <span className="field-name">{label}</span>
                  <span className="field-drag-hint">drag →</span>
                </div>
              ))}
            </div>

            <div className="placed-fields-list">
              <h5>
                Placed Fields
                <span className="field-count">{placedFields.length}</span>
              </h5>

              {placedFields.length === 0 ? (
                <p className="no-fields">No fields placed yet.<br />Drag a field onto the PDF.</p>
              ) : (
                placedFields.map(field => {
                  const { icon, name } = fieldLabel(field.type);
                  return (
                    <div key={field.id} className="placed-field-item">
                      <span className="field-type-badge">{icon} {name}</span>
                      <span className="field-location">
                        Pg {field.page} · {field.x.toFixed(0)}%, {field.y.toFixed(0)}%
                        <br />
                        <span style={{ fontSize: 10, color: '#999' }}>
                          {field.width.toFixed(0)}% × {field.height.toFixed(0)}%
                        </span>
                      </span>
                      <button onClick={() => removeField(field.id)} className="btn-remove">×</button>
                    </div>
                  );
                })
              )}
            </div>

            <div className="palette-actions">
              <button onClick={onCancel} className="btn-secondary btn-full">
                ← Cancel
              </button>
              <button
                onClick={handleComplete}
                className="btn-primary btn-full"
                disabled={placedFields.length === 0}
              >
                Continue to Send →
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default PdfUploadEditor;
